/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback, useRef} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {CreateJob, DownloadJobTasks} from '@ion/core/src/types/entities';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {DeleteIcon} from '@ion/dls/src/icons/DeleteIcon';
import {RemoveIcon} from '@ion/dls/src/icons/RemoveIcon';
import {SortDownIcon} from '@ion/dls/src/icons/SortDownIcon';
import {SortUpIcon} from '@ion/dls/src/icons/SortUpIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {checkIsEmptyTaskField, TaskRowActionTypes} from './helpers';

// defines the job task row editor prop definitions ...
export interface JobTaskRowEditorProps {
  index: number;
  tasks: CreateJob;
  removeDependency?: Function;
  setRemoveTaskValue: Function;
  navigateRows: (index: number, nav: string, tasks: CreateJob) => void;
};

const useStyles = makeStyles(() => ({
  menuButton : {
    paddingLeft : '0px',
    minWidth    : '0px'
  },
  disableItem : {
    opacity : '0.5',
    cursor  : 'default'
  }
}));

/**
 * Defines the JobTaskRowEditor component. This component contains the cell actions
 * for the job tasks table rows.
 *
 * ```typescript
 *
 *   import {JobTaskRowEditor} from './JobTaskRowEditor';
 *
 *   <JobTaskRowEditor .../>
 *
 * ```
 */
export const JobTaskRowEditor = ({
  index,
  tasks,
  removeDependency,
  navigateRows,
  setRemoveTaskValue
}: JobTaskRowEditorProps) => {
  const classes           = useStyles();
  const cellMenuRef       = useRef(null);
  const jobTasks          = tasks?.tasks;
  const task              = jobTasks[index]?.task || {};
  const nextTask          = jobTasks[index+1]?.task || {};
  const prevTask          = jobTasks[index-1]?.task || {};
  const dependentTask     = task?.metaData?.dependency?.index;
  const hasDependentTask  = dependentTask !== undefined;
  const childrenTask      = task?.children;
  const disableMoveUp     = dependentTask === index - 1;
  const disableMoveDown   = childrenTask?.includes(jobTasks[index+1]?.id);
  const fieldTranslations = translate('jobcreate:actionMenuItems', {returnObjects: true});
  const tooltipTitlePath  = translate('jobcreate:dependentTask', {returnObjects: true});
  const isDisableMoveUp   = checkIsEmptyTaskField(task) || checkIsEmptyTaskField(prevTask);
  const isDisableMoveDown = checkIsEmptyTaskField(task) || checkIsEmptyTaskField(nextTask);

  function closeMenu() {
    cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
  };

  const onAction = useCallback(function(type: string) {
    return function() {
      let shouldClose = true;

      switch (type) {
        case TaskRowActionTypes.DELETE:
          setRemoveTaskValue({index, tasks: jobTasks});
          break;
        case TaskRowActionTypes.REMOVE_DEPENDENCY:
          removeDependency({
            tasks     : {tasks: jobTasks},
            taskIndex : index
          });
          break;
        case TaskRowActionTypes.MOVE_UP:
          if (!disableMoveUp) {
            navigateRows(index, 'up', tasks);
          } else {
            shouldClose = false;
          };
          break;
        case TaskRowActionTypes.MOVE_DOWN:
          if (!disableMoveDown) {
            navigateRows(index, 'down', tasks);
          } else {
            shouldClose = false;
          };
          break;
        default:
          shouldClose = false;
      };

      if (shouldClose) {
        closeMenu();
      }
    };
  }, [index, tasks]);

  return (
    <CellMenu ref={cellMenuRef} menuItems={
      <div>
        {<CellMenuItem icon={<DeleteIcon />}
          onClick={onAction(TaskRowActionTypes.DELETE)}
          label={`${fieldTranslations?.delete}`} />}
        {(hasDependentTask) && <CellMenuItem icon={<RemoveIcon color={dlsColor('grey2')} />}
          onClick={onAction(TaskRowActionTypes.REMOVE_DEPENDENCY)}
          label={`${fieldTranslations?.removeDependency}`} />}
        {(index > 0) && <CellMenuItem icon={<SortUpIcon />}
          onClick={onAction(TaskRowActionTypes.MOVE_UP)}
          title={disableMoveUp && tooltipTitlePath?.moveUpWarning}
          customStyle={disableMoveUp && classes.disableItem}
          disabled={isDisableMoveUp}
          disableRipple={disableMoveUp}
          label={`${fieldTranslations?.moveUp}`} />}
        {(index < jobTasks?.length - 1) && <CellMenuItem icon={<SortDownIcon />}
          onClick={onAction(TaskRowActionTypes.MOVE_DOWN)}
          title={disableMoveDown && tooltipTitlePath?.insideChildWarning}
          customStyle={disableMoveDown && classes.disableItem}
          disabled={isDisableMoveDown}
          disableRipple={disableMoveDown}
          label={`${fieldTranslations?.moveDown}`} />}
      </div>
    } className={classes.menuButton} />
  );
};

export default memo(JobTaskRowEditor);
